import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from "rxjs";
import { environment } from "../../environments/environment";
import { ChatbotRequest, ChatbotResponse } from "../dashboard/interfaces/chatbot.interface";
import { ClientPriceGroup } from "../dashboard/interfaces/client-price-group.interface";
import { Client } from "../dashboard/interfaces/client.interface";
import { Customer } from "../dashboard/interfaces/customer.interface";
import { CylinderSystem } from "../dashboard/interfaces/cylinder-system.interface";
import { LockingPlan } from "../dashboard/interfaces/locking-plan.interface";

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  isLoadingSubject = new BehaviorSubject(false);

  setIsLoading(value: boolean) {
    this.isLoadingSubject.next(value);
  }

  constructor(private http: HttpClient) {}

  getStorageImageBlob(imageName: string): Observable<Blob> {
    return this.http.get(`${environment.apiURL}/api/storage/images/${imageName}`, { responseType: 'blob' });
  }

  login(email: string, password: string ) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/auth/login`, {email, password}, {
      headers: {'skip': 'true'}
    }).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  logout(refreshToken: string ) {
    this.setIsLoading(true);
    const accessToken = localStorage.getItem('accessToken');
    let headers = new HttpHeaders({
      'skipAuthHandling': 'true'
    });

    if (accessToken) {
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }

    return this.http.post<any>(`${environment.apiURL}/api/auth/logout`, {refreshToken}, {
      headers
    }).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  forgotPassword(email: string) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/users/forgot-password-request`, {email}, {
      headers: {'skip': 'true'}
    }).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  resetPassword(confirmationToken: string, password: string) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/users/reset-password`, {password, confirmationToken}, {
      headers: {'skip': 'true'}
    }).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getAccessToken(refreshToken: string) {
    this.setIsLoading(true);
    let headers = new HttpHeaders({
      'Authorization': 'Bearer ' + refreshToken,
      'skipSettingAccessToken': 'true'
    });
    return this.http.post(
      `${environment.apiURL}/api/auth/refresh-token`,
      {},
      {responseType: 'text', headers}).pipe(tap(() => {
      this.setIsLoading(false);
    }))
  }

  getClients() {
    this.setIsLoading(true);
    return this.http.get<Client[]>(`${environment.apiURL}/api/clients`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getClient(id: number) {
    this.setIsLoading(true);
    return this.http.get<Client>(`${environment.apiURL}/api/clients/${id}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }


  getClientLockingPlanCylinderSystems(clientId: number) {
    this.setIsLoading(true);
    return this.http.get<CylinderSystem[]>(`${environment.apiURL}/api/clients/${clientId}/locking-plan-cylinder-systems`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }



  getClientLockingPlanCylinderSystem(clientId: number, cylinderSystemId: number) {
    this.setIsLoading(true);
    return this.http.get<CylinderSystem[]>(`${environment.apiURL}/api/clients/${clientId}/locking-plan-cylinder-systems/${cylinderSystemId}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }


  getClientKeyedAlikeLockingPlanCylinderSystems(id: number) {
    this.setIsLoading(true);
    return this.http.get<CylinderSystem[]>(`${environment.apiURL}/api/clients/${id}/keyed-alike-locking-plan-cylinder-systems`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }



  // getClientProfilings() {
  //   this.setIsLoading(true);
  //   return this.http.get<ClientProfiling[]>(`${environment.apiURL}/api/clients/profilings`).pipe(tap(() => {
  //     this.setIsLoading(false);
  //   }));
  // }

  getClientPriceGroups() {
    this.setIsLoading(true);
    return this.http.get<ClientPriceGroup[]>(`${environment.apiURL}/api/prices/client-price-groups`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getCylinderSystemClientPriceGroups(cylinderSystemId: number) {
    this.setIsLoading(true);
    return this.http.get<ClientPriceGroup[]>(`${environment.apiURL}/api/prices/cylinder-system-client-price-groups/${cylinderSystemId}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getClientPriceGroup(id: number) {
    this.setIsLoading(true);
    return this.http.get<ClientPriceGroup>(`${environment.apiURL}/api/prices/client-price-groups/${id}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  createClientPriceGroup(data: any) {
    this.setIsLoading(true);
    return this.http.post<ClientPriceGroup>(`${environment.apiURL}/api/prices/client-price-groups`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }


  updateClientPriceGroup(id: number, data: any) {
    this.setIsLoading(true);
    return this.http.put<any>(`${environment.apiURL}/api/prices/client-price-groups/${id}`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  deleteClientPriceGroup(id: number) {
    this.setIsLoading(true);
    return this.http.delete<any>(`${environment.apiURL}/api/prices/client-price-groups/${id}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }


  getPrices() {
    this.setIsLoading(true);
    return this.http.get<any[]>(`${environment.apiURL}/api/prices`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  importPrices(clientPriceGroupId: number, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post(`${environment.apiURL}/api/prices/client-price-groups/${clientPriceGroupId}/import-prices`, formData);
  }

  getCustomers() {
    this.setIsLoading(true);
    return this.http.get<Customer[]>(`${environment.apiURL}/api/customers`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getCustomer(id: number) {
    this.setIsLoading(true);
    return this.http.get<Customer>(`${environment.apiURL}/api/customers/${id}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  createCustomer(data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/customers`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  updateCustomer(id: number, data: any) {
    this.setIsLoading(true);
    return this.http.put<any>(`${environment.apiURL}/api/customers/${id}`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }


  setCustomerMainAddress(id: number, data: any) {
    this.setIsLoading(true);
    return this.http.put<any>(`${environment.apiURL}/api/addresses/customer/${id}/main-address`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }


  deleteCustomerAddress(id: number, addressId: number) {
    this.setIsLoading(true);
    return this.http.delete<any>(`${environment.apiURL}/api/addresses/customer/${id}/addresses/${addressId}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  createCustomerAddress(id: number, addressData: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/addresses/customer/${id}/addresses`, addressData).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }



  createClient(data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/clients`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }


  updateClient(id: number, data: any) {
    this.setIsLoading(true);
    return this.http.put<any>(`${environment.apiURL}/api/clients/${id}`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }


  setClientMainAddress(id: number, data: any) {
    this.setIsLoading(true);
    return this.http.put<any>(`${environment.apiURL}/api/clients/${id}/main-address`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }


  deleteClientAddress(clientId: number, addressId: number) {
    this.setIsLoading(true);
    return this.http.delete<any>(`${environment.apiURL}/api/addresses/client/${clientId}/addresses/${addressId}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }


  createClientAddress(clientId: number, addressData: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/addresses/client/${clientId}/addresses`, addressData).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }



  createAddress(addressData: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/addresses`, addressData).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }



  getServerStatus() {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/health`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }



  getAppSettings() {
    let headers = new HttpHeaders({
    });
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/app-settings`, {headers}).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getUsers() {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/users`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getUser(id: number) {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/users/${id}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getLockingPlans() {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/locking-plans`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getLockingPlan(uuid: string) {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/locking-plans/${uuid}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  migrateLockingPlanCylinderSystem(lockingPlanId: string, cylinderSystemId: number) {
    this.setIsLoading(true);
    return this.http.post<LockingPlan>(`${environment.apiURL}/api/locking-plans/migrate-cylinder-system`, {
      lockingPlanId,
      cylinderSystemId
    }).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getLockingPlanQuote(uuid: string) {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/prices/locking-plan-quote/${uuid}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }


  getSalesLockingPlanQuote(uuid: string, clientPriceGroupId: number) {
    this.setIsLoading(true);
    const options = { params: new HttpParams().set('clientPriceGroupId', clientPriceGroupId) };
    return this.http.get<any>(`${environment.apiURL}/api/prices/sales-locking-plan-quote/${uuid}`, options).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }


  getLockingPlanBackOrderQuote(uuid: string) {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/prices/locking-plan-back-order-quote/${uuid}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getLockingPlanAddressGroups(lockingPlanId: string) {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/addresses/locking-plan-address-groups/${lockingPlanId}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getSalesLockingPlans() {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/locking-plans/sales`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getSalesLockingPlan(uuid: string) {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/locking-plans/sales/${uuid}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getBackOrderLockingPlans() {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/locking-plans/back-order`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getSalesBackOrderLockingPlans() {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/locking-plans/back-order/sales`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  generateLockingPlanPrices(uuid: string, data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/locking-plans/${uuid}/prices`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }


  orderLockingPlan(uuid: string, data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/prices/locking-plan-quote/${uuid}/order`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }



  getLockingPlanBackOrders() {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/locking-plan-back-orders`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getSalesLockingPlanBackOrders() {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/locking-plan-back-orders/sales`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getLockingPlanBackOrder(uuid: string) {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/locking-plan-back-orders/${uuid}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getSalesLockingPlanBackOrder(uuid: string) {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/locking-plan-back-orders/sales/${uuid}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  generateLockingPlanBackOrderPrices(uuid: string, data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/locking-plan-back-orders/${uuid}/prices`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  createLockingPlanBackOrder(data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/locking-plan-back-orders`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  createSalesLockingPlanBackOrder(data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/locking-plan-back-orders/sales`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  updateLockingPlanBackOrder(uuid: string, data: any) {
    this.setIsLoading(true);
    return this.http.put<any>(`${environment.apiURL}/api/locking-plan-back-orders/${uuid}`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  deleteLockingPlanBackOrder(uuid: string) {
    this.setIsLoading(true);
    return this.http.delete<any>(`${environment.apiURL}/api/locking-plan-back-orders/${uuid}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }


  getLockingPlanBackOrderPdf(uuid: string) {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/locking-plan-back-orders/${uuid}/pdf`, { responseType: 'blob' as 'json'}).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getSalesLockingPlanBackOrderPdf(uuid: string) {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/locking-plan-back-orders/sales/${uuid}/pdf`, { responseType: 'blob' as 'json'}).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getSalesLockingPlanBackOrderQuote(uuid: string, clientPriceGroupId: number) {
    this.setIsLoading(true);
    const options = { params: new HttpParams().set('clientPriceGroupId', clientPriceGroupId) };
    return this.http.get<any>(`${environment.apiURL}/api/prices/sales-locking-plan-back-order-quote/${uuid}`, options).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  assignSalesLockingPlanBackOrderToClient(data: any, uuid: string) {
    this.setIsLoading(true);
    return this.http.put<any>(`${environment.apiURL}/api/locking-plan-back-orders/sales/${uuid}/assign`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  orderLockingPlanBackOrder(uuid: string, data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/prices/locking-plan-back-order-quote/${uuid}/order`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getKeyBackOrders() {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/key-back-orders`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getKeyBackOrder(uuid: string) {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/key-back-orders/${uuid}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  createKeyBackOrder(data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/key-back-orders`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  updateKeyBackOrder(uuid: string, data: any) {
    this.setIsLoading(true);
    return this.http.put<any>(`${environment.apiURL}/api/key-back-orders/${uuid}`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }


  getLockingPlanPdf(uuid: string) {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/locking-plans/${uuid}/pdf`, { responseType: 'blob' as 'json'}).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }



  getLockingPlanXlsx(uuid: string) {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/locking-plans/${uuid}/xlsx`, { responseType: 'blob' as 'json'}).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  createLockingPlan(data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/locking-plans`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  createSalesLockingPlan(data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/locking-plans/sales`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  generateSalesLockingPlan(data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/locking-plans/sales/generate`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  ragChat(data: ChatbotRequest) {
    this.setIsLoading(true);
    return this.http.post<ChatbotResponse>(`${environment.apiURL}/api/rag/chat`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  indexRagSystemDocuments(data: FormData) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/rag/index-system-documents/upload`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getRagIndexedSystems() {
    this.setIsLoading(true);
    return this.http.get<any[]>(`${environment.apiURL}/api/rag/indexed-systems`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getRagIndexedSystemChunks(systemId: number) {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/rag/indexed-systems/${systemId}/chunks`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  updateGeneratedSalesLockingPlan(data: any, uuid: string) {
    this.setIsLoading(true);
    return this.http.put<any>(`${environment.apiURL}/api/locking-plans/sales/${uuid}/generated`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  assignSalesLockingPlanToClient(data: any, uuid: string) {
    this.setIsLoading(true);
    return this.http.put<any>(`${environment.apiURL}/api/locking-plans/sales/${uuid}/assign`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  updateLockingPlan(uuid: string, data: any) {
    this.setIsLoading(true);
    return this.http.put<any>(`${environment.apiURL}/api/locking-plans/${uuid}`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  updateLockingPlanName(uuid: string, name: string) {
    this.setIsLoading(true);
    return this.http.put<any>(`${environment.apiURL}/api/locking-plans/${uuid}/name`, { name }).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  deleteLockingPlan(uuid: string) {
    this.setIsLoading(true);
    return this.http.delete<any>(`${environment.apiURL}/api/locking-plans/${uuid}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }


  importLockingPlanCsv(data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/locking-plans/import-csv`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  importLockingPlanXlsx(data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/locking-plans/import-xlsx`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  importLockingPlanPdf(data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/locking-plans/import-pdf`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  sendLockingPlan(uuid: string, data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/locking-plans/${uuid}/send-locking-plan`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  sendLockingPlanToCustomer(uuid: string, data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/locking-plans/${uuid}/send-locking-plan-customer`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  lockLockingPlan(uuid: string) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/locking-plans/${uuid}/lock`, {}).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  unlockLockingPlan(uuid: string) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/locking-plans/${uuid}/unlock`, {}).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }


  getKeyedAlikeLockingPlans() {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/keyed-alike-locking-plans`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getKeyedAlikeLockingPlan(uuid: string) {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/keyed-alike-locking-plans/${uuid}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  createKeyedAlikeLockingPlan(data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/keyed-alike-locking-plans`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  updateKeyedAlikeLockingPlan(uuid: string, data: any) {
    this.setIsLoading(true);
    return this.http.put<any>(`${environment.apiURL}/api/keyed-alike-locking-plans/${uuid}`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  deleteKeyedAlikeLockingPlan(uuid: string) {
    this.setIsLoading(true);
    return this.http.delete<any>(`${environment.apiURL}/api/keyed-alike-locking-plans/${uuid}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  generateKeyedAlikeLockingPlanPrices(uuid: string, data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/keyed-alike-locking-plans/${uuid}/prices`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  orderKeyedAlikeLockingPlan(uuid: string, data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/keyed-alike-locking-plans/${uuid}/order`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getKeyedAlikeLockingPlanPdf(uuid: string) {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/keyed-alike-locking-plans/${uuid}/pdf`, { responseType: 'blob' as 'json'}).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  signUpUser(data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/users/sign-up`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  createUser(data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/users`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  updateUser(data: any, id: number) {
    this.setIsLoading(true);
    return this.http.put<any>(`${environment.apiURL}/api/users/${id}`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getCylinderSystems() {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/cylinders/cylinder-systems`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getCylinderSystem(id: number) {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/cylinders/cylinder-systems/${id}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  updateCylinderSystem(id: number, data: any) {
    this.setIsLoading(true);
    return this.http.put<any>(`${environment.apiURL}/api/cylinders/cylinder-systems/${id}`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  deleteCylinderSystem(id: number) {
    this.setIsLoading(true);
    return this.http.delete<any>(`${environment.apiURL}/api/cylinders/cylinder-systems/${id}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  createCylinderSystem(data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/cylinders/cylinder-systems`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getCylinderTypes() {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/cylinders/cylinder-types`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getCylinderType(id: number) {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/cylinders/cylinder-types/${id}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  updateCylinderType(id: number, data: any) {
    this.setIsLoading(true);
    return this.http.put<any>(`${environment.apiURL}/api/cylinders/cylinder-types/${id}`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  createCylinderType(data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/cylinders/cylinder-types`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  deleteCylinderType(id: number) {
    this.setIsLoading(true);
    return this.http.delete<any>(`${environment.apiURL}/api/cylinders/cylinder-types/${id}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getCylinderSizes() {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/cylinders/cylinder-sizes`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  createCylinderSize(data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/cylinders/cylinder-sizes`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  deleteCylinderSize(id: number) {
    this.setIsLoading(true);
    return this.http.delete<any>(`${environment.apiURL}/api/cylinders/cylinder-sizes/${id}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getCylinderMaterials() {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/cylinders/cylinder-materials`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }



  getCylinderMaterial(id: number) {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/cylinders/cylinder-materials/${id}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }


  updateCylinderMaterial(id: number, data: any) {
    this.setIsLoading(true);
    return this.http.put<any>(`${environment.apiURL}/api/cylinders/cylinder-materials/${id}`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }


  createCylinderOption(data: any) {
    this.setIsLoading(true);
    return this.http.post<any>(`${environment.apiURL}/api/cylinders/cylinder-options`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  getCylinderOptions() {
    this.setIsLoading(true);
    return this.http.get<any>(`${environment.apiURL}/api/cylinders/cylinder-options`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  updateCylinderOption(id: number, data: any) {
    this.setIsLoading(true);
    return this.http.put<any>(`${environment.apiURL}/api/cylinders/cylinder-options/${id}`, data).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }

  deleteCylinderOption(id: number) {
    this.setIsLoading(true);
    return this.http.delete<any>(`${environment.apiURL}/api/cylinders/cylinder-options/${id}`).pipe(tap(() => {
      this.setIsLoading(false);
    }));
  }


}
